import { Lex } from './formula.lexing';
import { Parse } from './formula.parsing';
import type {
  BuiltIn,
  ErrorCorrection,
  ExecutionResult,
  NtTree,
  Terms,
} from './formula.types';
import {
  IsApplication,
  IsBinaryExpression,
  isExecutionResult,
  TokenTypes,
} from './formula.types';

// We have to disable no any because of the inherently dynamic nature of
// interpreting an untyped language.
/* eslint @typescript-eslint/no-explicit-any: 0 */

/**
 * Lexes, parses, and executes the provided formula in the given context.
 * @param formula A string containing the formula to be executed.
 * @param context An object whose keys and values are the bindings available to the interpreter.
 * @returns The value of the formula in the provided context.
 */
export function Execute(
  formula: string,
  context: Record<string, number | string | boolean> = {},
): ExecutionResult {
  const rootTerm = Parse(formula);

  // if (rootTerm.success) won't work because of strictNullChecks: false
  if (rootTerm.success === true) {
    // Parsing succeeded; build a runner (see below) and generate a result.
    const runner = new Runner(formula, context);
    const provisionalResult = runner.Execute.bind(runner)(rootTerm.tree);

    // If the result is a success, we can return it.
    if (provisionalResult.success) return provisionalResult;

    // If the result failed, we need to find any missing fields and add them to what we return.
    return {
      ...provisionalResult,
      missingFields: Object.keys(GetIdentifiers(formula, context).undefined).filter(
        (id) => !/^\d+(\.\d+)?\s*$/.test(id), // Numbers shouldn't count as undefined because their value is supplied by the executor.
      ),
    } as ExecutionResult;
  } else {
    // Parsing failed. We can return the error.
    return {
      ...rootTerm,
      success: false,
      missingFields: [], // We don't know; there's a deeper problem involved.
    } as ExecutionResult;
  }
}

/**
 * Retrieves the list of used identifiers from a formula and categorises them into builtin, defined, and undefined based on the provided context.
 * @param formula The formula from which to retrieve the identifiers.
 * @param context The context to determine which identifiers are defined.
 */
export function GetIdentifiers(formula: string, context?: object) {
  context ??= {};
  context = Object.fromEntries(
    Object.entries(context).map((kvp) => [kvp[0].toUpperCase(), kvp[1]]),
  );

  // Cleans up the name of a symbol.
  function symbolName(name: string) {
    if (name.startsWith('{') && name.endsWith('}')) {
      return name.slice(1, name.length - 1);
    } else if (name.startsWith('`') && name.endsWith('`')) {
      return name.slice(1, name.length - 1);
    } else {
      return name;
    }
  }

  // Find all identifiers.
  const identifiers = Object.entries(Lex(formula))
    .flatMap((kvp) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_, v] = kvp;
      return v
        .filter((tkn) => tkn.token === TokenTypes.ID)
        .map((tkn) => ({
          ...tkn,
          lexeme: symbolName(tkn.lexeme).toLowerCase(),
          end: tkn.start + tkn.lexeme.length,
        }));
    })
    .reduce(
      (prev, cur) => {
        (prev[cur.lexeme] ||= []).push({ start: cur.start, end: cur.end });
        return prev;
      },
      {} as Record<string, { start: number; end: number }[]>,
    );

  // Check the basis to figure out which identifiers are built-in.
  const defaultBasisIds = Object.keys(Runner.defaultBasis)
    .map((k) => k.toUpperCase())
    .filter((k) => !Object.keys(context).includes(k));

  const usedBuiltins = Object.fromEntries(
    Object.entries(identifiers).filter((kvp) => defaultBasisIds.includes(kvp[0].toUpperCase())),
  );
  const defined = Object.fromEntries(
    Object.entries(identifiers).filter((kvp) =>
      Object.keys(context).includes(kvp[0].toUpperCase()),
    ),
  );
  // Identifiers not built-in or defined are undefined.
  const undefinedIds = Object.entries(identifiers).filter(
    (kvp) => !(kvp[0] in usedBuiltins || kvp[0] in defined),
  );

  return {
    builtins: usedBuiltins,
    defined: defined,
    undefined: Object.fromEntries(undefinedIds),
  };
}

class Runner {
  /**
   * This is the default basis: the environment that gets augmented by whatever context is provided at runtime.
   * Any "global" functions or constants (e.g. AVG, PI, etc.) can go in here.
   */
  static defaultBasis: Record<string, any> = {
    TRUE: true,
    FALSE: false,

    IF: {
      implementation: (
        args: any[],
        extents: { start: number; end: number },
        inputArgs: { lexeme: string; extents: { start: number; end: number } }[],
      ) => {
        const conditionValid =
          args &&
          args.length > 0 &&
          (typeof args[0] === 'boolean' ||
            (typeof args[0] === 'string' && args[0].toUpperCase() === 'TRUE') ||
            (typeof args[0] === 'string' && args[0].toUpperCase() === 'FALSE'));

        if (!args || args.length !== 3) {
          const errors = [
            {
              message: 'Use the correct number of arguments.',
              correction: {
                replaceFrom: extents.start,
                replaceTo: extents.end,
                replaceWith: `IF(${conditionValid ? inputArgs[0]?.lexeme : 'TRUE'}, ${args.length > (conditionValid ? 1 : 0) ? inputArgs[conditionValid ? 1 : 0]?.lexeme : 'ID'}, ${args.length > (conditionValid ? 2 : 1) ? inputArgs[conditionValid ? 2 : 1]?.lexeme : 'ID'})`,
              } as ErrorCorrection,
            },
          ];

          return {
            success: false,
            errors,
          };
        } else if (!conditionValid) {
          return {
            success: false,
            errors: [
              {
                message: 'Provide a boolean value for the first argument to IF.',
                correction: {
                  replaceFrom: inputArgs[0]?.extents.start ?? extents.start,
                  replaceTo: inputArgs[0]?.extents.end ?? extents.end,
                  replaceWith: 'TRUE',
                },
              },
            ],
          } as ExecutionResult;
        }

        if (args[0] && args[0].toUpperCase && args[0].toUpperCase() === 'FALSE') args[0] = false;

        return {
          success: true,
          value: args[0] ? args[1] : args[2],
        } as ExecutionResult;
      },
    } as BuiltIn,

    AND: {
      implementation: (args: any[]) => {
        return {
          success: true,
          value: args
            .flat()
            .map((i) => !!i)
            .every((i) => i),
        } as ExecutionResult;
      },
    } as BuiltIn,

    OR: {
      implementation: (args: any[]) => {
        return {
          success: true,
          value:
            args.length === 0 ||
            args
              .flat()
              .map((i) => !!i)
              .some((i) => i),
        } as ExecutionResult;
      },
    } as BuiltIn,

    NOT: {
      arity: 1,
      implementation: (
        args: any[],
        extents: { start: number; end: number },
        inputArgs: { lexeme: string; extents: { start: number; end: number } }[],
      ) => {
        if (args.length !== 1) {
          return {
            success: false,
            errors: [
              {
                message: 'Use the correct number of arguments.',
                correction: {
                  replaceFrom: extents.start,
                  replaceTo: extents.end,
                  replaceWith: `NOT(${inputArgs.length > 0 ? inputArgs[0]?.lexeme : 'TRUE'})`,
                },
              },
            ],
          } as ExecutionResult;
        }

        return {
          success: true,
          value: !args[0],
        } as ExecutionResult;
      },
    } as BuiltIn,

    AVG: {
      implementation: (
        args: any[],
        extents: { start: number; end: number },
      ) => {
        args = args.flat(Infinity);

        // Check if all arguments are valid numbers (matching pattern from binary operators)
        for (const arg of args) {
          if (typeof arg !== 'number' || Number.isNaN(arg)) {
            return {
              success: false,
              position: extents.start,
              errors: [],
              missingFields: [],
            } as ExecutionResult;
          }
        }

        if (args.length === 0) {
          return {
            success: true,
            value: 0,
          } as ExecutionResult;
        }

        let sum = 0;
        args.forEach((i) => (sum += i * 1)); // Necessary to cast numeric strings.
        return {
          success: true,
          value: sum / args.length,
        } as ExecutionResult;
      },
    } as BuiltIn,

    MEDIAN: {
      implementation: (
        args: any[],
        extents: { start: number; end: number },
      ) => {
        args = args.flat(Infinity);

        // Check if all arguments are valid numbers (matching pattern from binary operators)
        for (const arg of args) {
          if (typeof arg !== 'number' || Number.isNaN(arg)) {
            return {
              success: false,
              position: extents.start,
              errors: [],
              missingFields: [],
            } as ExecutionResult;
          }
        }

        if (args.length === 0) {
          return { success: true, value: 0 } as ExecutionResult;
        }

        args.sort();
        const midpoint = Math.floor(args.length / 2);

        if (args.length % 2 === 0) {
          return {
            success: true,
            value: (args[midpoint] + args[midpoint + 1]) / 2,
          } as ExecutionResult;
        } else {
          return { success: true, value: args[midpoint] } as ExecutionResult;
        }
      },
    } as BuiltIn,

    PI: Math.PI,

    POW: {
      arity: 2,
      implementation: (
        args: any[],
        extents: { start: number; end: number },
        inputArgs: { lexeme: string; extents: { start: number; end: number } }[],
      ): ExecutionResult => {
        if (args.length !== 2) {
          return {
            success: false,
            errors: [
              {
                message: 'Use the correct number of arguments.',
                correction: {
                  replaceFrom: extents.start,
                  replaceTo: extents.end,
                  replaceWith: `POW(${inputArgs.length > 0 ? inputArgs[0]?.lexeme : 'ID'}, ${inputArgs.length > 1 ? inputArgs[1]?.lexeme : 'ID'})`,
                },
              },
            ],
          } as ExecutionResult;
        }

        return {
          success: true,
          value: Math.pow(args[0], args[1]),
        } as ExecutionResult;
      },
    } as BuiltIn,

    MIN: {
      implementation: (args: any[]) => {
        return { success: true, value: Math.min(...args.flat(Infinity)) } as ExecutionResult;
      },
    } as BuiltIn,

    MAX: {
      implementation: (args: any[]) => {
        return { success: true, value: Math.max(...args.flat(Infinity)) } as ExecutionResult;
      },
    } as BuiltIn,

    MATCH: {
      arity: 2,
      implementation: (
        args: any[],
        extents: { start: number; end: number },
        inputArgs: { lexeme: string; extents: { start: number; end: number } }[],
      ) => {
        if (args.length !== 2) {
          return {
            success: false,
            errors: [
              {
                message: 'Use the correct number of arguments.',
                correction: {
                  replaceFrom: extents.start,
                  replaceTo: extents.end,
                  replaceWith: `MATCH(${inputArgs.length > 0 ? inputArgs[0]?.lexeme : 'ID'}, ${inputArgs.length > 1 ? inputArgs[1]?.lexeme : 'ID'})`,
                },
              },
            ],
          } as ExecutionResult;
        }

        return { success: true, value: new RegExp(args[0], 'i').test(args[1]) } as ExecutionResult;
      },
    } as BuiltIn,
  };

  basis: Record<string, any> = {};
  input: string;

  constructor(input: string, context?: Record<string, any>) {
    this.input = input;
    this.basis = { ...Runner.defaultBasis };

    for (const key in context ?? {}) {
      // Copies context items into the basis for this specific run, uppercasing for case insensitivity.
      this.basis[key.toUpperCase()] = context?.[key];
    }
  }

  /** Find what identifiers are missing. */
  private GetMissing(this: Runner, identifiers: string[]): string[] {
    return identifiers.filter((id) => !Object.keys(this.basis).includes(id));
  }

  /** Default entry point for execution. Delegates to term-appropriate execution methods. */
  Execute(
    this: Runner,
    term: Terms.Value | Terms.Application | Terms.BinaryExpression,
  ): ExecutionResult {
    if (IsBinaryExpression(term)) {
      return this.ExecBinExpr.bind(this)(term);
    } else if (IsApplication(term)) {
      return this.ExecApp.bind(this)(term);
    } else {
      return this.ExecValue.bind(this)(term);
    }
  }

  /** Executes a value (e.g. a literal or context reference). */
  ExecValue(this: Runner, id: Terms.Value): ExecutionResult {
    let retval: string | number | boolean;
    const identifiers: { lexeme: string; extents: { start: number; end: number } }[] = [];

    // Regex: An optional +/- sign followed by ((at least one digit) or (any number of digits followed by a . and at least one digit)).
    if (id.value.match(/^[+-]?(?:\d+\.?|\d*\.\d+)$/)) {
      // Number
      retval = parseFloat(id.value);
    }
    // Regex: Any string of anything starting and ending with a matched pair of "s or 's.
    else if (id.value.match(/^(['"]).*\1$/)) {
      // String literal
      retval = id.value.slice(1, id.value.length - 1);
    }
    // Regex: Any string of anything starting and ending with a matched pair of `s or { and }.
    else if (id.value.match(/^`.*`$/) || id.value.match(/^\{.*\}$/)) {
      // ID literal
      const identifier = id.value.slice(1, id.value.length - 1).toUpperCase();
      identifiers.push({ lexeme: identifier, extents: id.extents });

      if (identifier in this.basis) {
        retval = this.basis[identifier];
      } else retval = id.value.slice(1, id.value.length - 1);
    } else if (id.value.toUpperCase() in this.basis) {
      // Basis reference, likely a context-provided binding.
      const identifier = id.value.toUpperCase();
      retval = this.basis[identifier];
      identifiers.push({ lexeme: identifier, extents: id.extents });
    } else retval = id.value; // ID literal (e.g. RED or HIGH or something like that).

    return {
      success: true,
      value: retval,
      identifiers,
      extents: id.extents,
    };
  }

  /** Executes a function application. */
  ExecApp(this: Runner, app: Terms.Application): ExecutionResult {
    if (
      app.funcId in this.basis &&
      (!this.basis[app.funcId].arity || this.basis[app.funcId].arity === app.arguments.length)
    ) {
      // Found the function in the basis with the correct arity. Run it!

      // Calculate the arguments to the function.
      const args: ExecutionResult[] = app.arguments.map(this.Execute.bind(this));

      if (args.some((a) => !a.success)) {
        // One of the arguments failed. Just short-circuit out and don't bother continuing.
        return args.find((a) => !a.success) as ExecutionResult;
      }

      let value: ExecutionResult;
      try {
        // Run the function with the arguments.
        value = this.basis[app.funcId].implementation(
          args.filter((a) => a.success === true).map((a) => a.value),
          app.extents,
          app.arguments.map((a: NtTree) => ({
            lexeme: this.input.slice(a.extents.start, a.extents.end),
            extents: a.extents,
          })),
        );

        if (value.success !== true) {
          // Failed! Fix the error's position and return it.
          return {
            ...value,
            position: app.extents.start,
          };
        }
      } catch (ex) {
        return {
          success: false,
          position: app.extents.start,
          errors: [
            {
              message: `Error in builtin funtion. Please report the error to the developers: ${JSON.stringify(ex)}`,
              correction: { deleteFrom: app.extents.start, deleteTo: app.extents.end },
            },
          ],
          missingFields: [],
        };
      }

      return {
        success: true,
        value: isExecutionResult(value) ? value.value : value,
        identifiers: [
          {
            lexeme: app.funcId,
            extents: { start: app.extents.start, end: app.extents.start + app.funcId.length },
          },
          ...args.filter((a) => a.success === true).flatMap((a) => a.identifiers),
        ],
        extents: app.extents,
      };
    }

    // Gets the input string portion corresponding to a pair of extents. Used to get argument lexemes for passing to a Builtin.
    const getArg = (extents: { start: number; end: number }) =>
      this.input.slice(extents.start, extents.end).trim();

    if (app.funcId in this.basis) {
      // Found the function, but the arity is wrong.

      const expectedArgList: string[] = [];
      const expectedArity = this.basis[app.funcId].arity ?? 0;
      for (let i = 0; i < expectedArity; i++) {
        // Synthesize the missing arguments for the error message below.
        if (i < app.arguments.length) {
          const arg = app.arguments[i];
          if (arg) {
            expectedArgList.push(getArg(arg.extents));
          }
        } else {
          expectedArgList.push('ID');
        }
      }
      const functionSignature = `${app.funcId}(${expectedArgList.join(', ')})`;

      return {
        success: false,
        position: app.extents.start,
        errors: [
          {
            message: `Function ${functionSignature} expects ${expectedArity} arguments, but got ${app.arguments.length}.`,
            correction: {
              replaceFrom: app.extents.start,
              replaceTo: app.extents.end,
              replaceWith: functionSignature,
            },
          },
        ],
        missingFields: [],
      };
    }

    // If we get to this point, they have specified a function that we don't know about.
    const functionSignature = `${app.funcId}(${app.arguments.map((_, index) => {
      const arg = app.arguments[index];
      return arg ? getArg(arg.extents) : 'ID';
    }).join(', ')})`;

    // Typo correct up to half of the characters.
    let minimumEditDistance = Math.floor(app.funcId.length / 2) + 1;
    let minimumEditResult: string | null = null;

    if (app.funcId.length > 0 && !/^[\d.]+$/.test(app.funcId)) {
      // Regex: Any string of digits and .s that isn't empty.
      // Do not correct numbers.
      for (const id in this.basis) {
        const editDist = EditDistance(app.funcId, id);
        if (editDist < minimumEditDistance) {
          minimumEditDistance = editDist;
          minimumEditResult = id;
        }
      }
    }

    const correctedFunctionSignature = `${minimumEditResult}(${app.arguments.map((_, index) => {
      const arg = app.arguments[index];
      return arg ? getArg(arg.extents) : 'ID';
    }).join(', ')})`;

    return {
      success: false,
      position: app.extents.start,
      errors: [
        minimumEditResult === null
          ? {
              // Couldn't typo correct; it's likely they're just using something that doesn't exist.
              message: `Cannot find function ${functionSignature}.`,
              correction: {
                deleteFrom: app.extents.start + app.funcId.length,
                deleteTo: app.extents.end,
              },
            }
          : {
              // Found it. It's probably a typo.
              message: `Cannot find function ${functionSignature}. Did you mean ${correctedFunctionSignature}?`,
              correction: {
                replaceFrom: app.extents.start,
                replaceTo: app.extents.end,
                replaceWith: correctedFunctionSignature,
              },
            },
      ],
      missingFields: this.GetMissing.bind(this)([app.funcId]),
    };
  }

  /** Executes an infixed binary operator (e.g. 1 + 2, a mod b). */
  ExecBinExpr(this: Runner, expr: Terms.BinaryExpression): ExecutionResult {
    // Evaluate the left side and make sure it's valid.
    const leftArg = this.Execute.bind(this)(expr.leftExpr);
    if (!leftArg.success) return leftArg;

    // Evaluate the right side and make sure it's valid.
    const rightArg = this.Execute.bind(this)(expr.rightExpr);
    if (!rightArg.success) return rightArg;

    let retval;

    // We don't want arbitrary floating-point precision here for two reasons:
    // 1. We need 0.1 + 0.2 to equal 0.3 which isn't guaranteed at higher precisions in all browsers.
    // 2. We need the output to be of a reasonable length for the UI.
    const cleanFloatingPoint = (n: number) => Math.floor(n * 100000) / 100000;

    switch (expr.binOp) {
      case '+':
        // Make sure both arguments are numbers.
        if (typeof leftArg.value !== 'number')
          return {
            success: false,
            position: leftArg.extents.start,
            errors: [
              {
                message: `Expected a number on the left side of +, but got ${this.input.slice(leftArg.extents.start, leftArg.extents.end).trim()} (a ${typeof leftArg.value}) instead.`,
                correction: {
                  replaceFrom: leftArg.extents.start,
                  replaceTo: leftArg.extents.end,
                  replaceWith: '0 ',
                },
              },
            ],
            missingFields: [],
          };
        if (typeof rightArg.value !== 'number')
          return {
            success: false,
            position: rightArg.extents.start,
            errors: [
              {
                message: `Expected a number on the right side of +, but got ${this.input.slice(rightArg.extents.start, rightArg.extents.end).trim()} (a ${typeof rightArg.value}) instead.`,
                correction: {
                  replaceFrom: rightArg.extents.start,
                  replaceTo: rightArg.extents.end,
                  replaceWith: '0 ',
                },
              },
            ],
            missingFields: [],
          };
        retval = cleanFloatingPoint(leftArg.value + rightArg.value);
        break;

      case '-':
        // Make sure both arguments are numbers.
        if (typeof leftArg.value !== 'number')
          return {
            success: false,
            position: leftArg.extents.start,
            errors: [
              {
                message: `Expected a number on the left side of -, but got ${this.input.slice(leftArg.extents.start, leftArg.extents.end).trim()} (a ${typeof leftArg.value}) instead.`,
                correction: {
                  replaceFrom: leftArg.extents.start,
                  replaceTo: leftArg.extents.end,
                  replaceWith: '0 ',
                },
              },
            ],
            missingFields: [],
          };
        if (typeof rightArg.value !== 'number')
          return {
            success: false,
            position: rightArg.extents.start,
            errors: [
              {
                message: `Expected a number on the right side of -, but got ${this.input.slice(rightArg.extents.start, rightArg.extents.end).trim()} (a ${typeof rightArg.value}) instead.`,
                correction: {
                  replaceFrom: rightArg.extents.start,
                  replaceTo: rightArg.extents.end,
                  replaceWith: '0 ',
                },
              },
            ],
            missingFields: [],
          };
        retval = cleanFloatingPoint(leftArg.value - rightArg.value);
        break;

      case '*':
        // Make sure both arguments are numbers.
        if (typeof leftArg.value !== 'number') {
          return {
            success: false,
            position: leftArg.extents.start,
            errors: [
              {
                message: `Expected a number on the left side of *, but got ${this.input.slice(leftArg.extents.start, leftArg.extents.end).trim()} (a ${typeof leftArg.value}) instead.`,
                correction: {
                  replaceFrom: leftArg.extents.start,
                  replaceTo: leftArg.extents.end,
                  replaceWith: '1 ',
                },
              },
            ],
            missingFields: [],
          };
        }
        if (typeof rightArg.value !== 'number')
          return {
            success: false,
            position: rightArg.extents.start,
            errors: [
              {
                message: `Expected a number on the right side of *, but got ${this.input.slice(rightArg.extents.start, rightArg.extents.end).trim()} (a ${typeof rightArg.value}) instead.`,
                correction: {
                  replaceFrom: rightArg.extents.start,
                  replaceTo: rightArg.extents.end,
                  replaceWith: '1 ',
                },
              },
            ],
            missingFields: [],
          };
        retval = leftArg.value * rightArg.value;
        break;

      case '/':
        // Make sure both arguments are numbers.
        if (typeof leftArg.value !== 'number')
          return {
            success: false,
            position: leftArg.extents.start,
            errors: [
              {
                message: `Expected a number on the left side of /, but got ${this.input.slice(leftArg.extents.start, leftArg.extents.end).trim()} (a ${typeof leftArg.value}) instead.`,
                correction: {
                  replaceFrom: leftArg.extents.start,
                  replaceTo: leftArg.extents.end,
                  replaceWith: '0 ',
                },
              },
            ],
            missingFields: [],
          };
        if (typeof rightArg.value !== 'number')
          return {
            success: false,
            position: rightArg.extents.start,
            errors: [
              {
                message: `Expected a number on the right side of /, but got ${this.input.slice(rightArg.extents.start, rightArg.extents.end).trim()} (a ${typeof rightArg.value}) instead.`,
                correction: {
                  replaceFrom: rightArg.extents.start,
                  replaceTo: rightArg.extents.end,
                  replaceWith: '1 ',
                },
              },
            ],
            missingFields: [],
          };
        retval = rightArg.value === 0 ? '#DIV0' : leftArg.value / rightArg.value;
        break;

      case 'mod':
        // Make sure both arguments are numbers.
        if (typeof leftArg.value !== 'number')
          return {
            success: false,
            position: leftArg.extents.start,
            errors: [
              {
                message: `Expected a number on the left side of mod, but got ${this.input.slice(leftArg.extents.start, leftArg.extents.end).trim()} (a ${typeof leftArg.value}) instead.`,
                correction: {
                  replaceFrom: leftArg.extents.start,
                  replaceTo: leftArg.extents.end,
                  replaceWith: '0 ',
                },
              },
            ],
            missingFields: [],
          };
        if (typeof rightArg.value !== 'number')
          return {
            success: false,
            position: rightArg.extents.start,
            errors: [
              {
                message: `Expected a number on the right side of mod, but got ${this.input.slice(rightArg.extents.start, rightArg.extents.end).trim()} (a ${typeof rightArg.value}) instead.`,
                correction: {
                  replaceFrom: rightArg.extents.start,
                  replaceTo: rightArg.extents.end,
                  replaceWith: '1 ',
                },
              },
            ],
            missingFields: [],
          };
        retval = rightArg.value === 0 ? '#MOD0' : leftArg.value % rightArg.value;
        break;

      case '=':
        // THIS IS INTENTIONALLY COERCING!
        retval = leftArg == rightArg;
        break;

      case '>':
        // Make sure both arguments are numbers.
        if (typeof leftArg.value !== 'number')
          return {
            success: false,
            position: leftArg.extents.start,
            errors: [
              {
                message: `Expected a number on the left side of >, but got ${this.input.slice(leftArg.extents.start, leftArg.extents.end).trim()} (a ${typeof leftArg.value}) instead.`,
                correction: {
                  replaceFrom: leftArg.extents.start,
                  replaceTo: leftArg.extents.end,
                  replaceWith: '0 ',
                },
              },
            ],
            missingFields: [],
          };
        if (typeof rightArg.value !== 'number')
          return {
            success: false,
            position: rightArg.extents.start,
            errors: [
              {
                message: `Expected a number on the right side of >, but got ${this.input.slice(rightArg.extents.start, rightArg.extents.end).trim()} (a ${typeof rightArg.value}) instead.`,
                correction: {
                  replaceFrom: rightArg.extents.start,
                  replaceTo: rightArg.extents.end,
                  replaceWith: '1 ',
                },
              },
            ],
            missingFields: [],
          };
        retval = leftArg.value > rightArg.value;
        break;

      case '<':
        // Make sure both arguments are numbers.
        if (typeof leftArg.value !== 'number')
          return {
            success: false,
            position: leftArg.extents.start,
            errors: [
              {
                message: `Expected a number on the left side of <, but got ${this.input.slice(leftArg.extents.start, leftArg.extents.end).trim()} (a ${typeof leftArg.value}) instead.`,
                correction: {
                  replaceFrom: leftArg.extents.start,
                  replaceTo: leftArg.extents.end,
                  replaceWith: '0 ',
                },
              },
            ],
            missingFields: [],
          };
        if (typeof rightArg.value !== 'number')
          return {
            success: false,
            position: rightArg.extents.start,
            errors: [
              {
                message: `Expected a number on the right side of <, but got ${this.input.slice(rightArg.extents.start, rightArg.extents.end).trim()} (a ${typeof rightArg.value}) instead.`,
                correction: {
                  replaceFrom: rightArg.extents.start,
                  replaceTo: rightArg.extents.end,
                  replaceWith: '1 ',
                },
              },
            ],
            missingFields: [],
          };
        retval = leftArg.value < rightArg.value;
        break;

      case '>=':
        // Make sure both arguments are numbers.
        if (typeof leftArg.value !== 'number')
          return {
            success: false,
            position: leftArg.extents.start,
            errors: [
              {
                message: `Expected a number on the left side of >=, but got ${this.input.slice(leftArg.extents.start, leftArg.extents.end).trim()} (a ${typeof leftArg.value}) instead.`,
                correction: {
                  replaceFrom: leftArg.extents.start,
                  replaceTo: leftArg.extents.end,
                  replaceWith: '0 ',
                },
              },
            ],
            missingFields: [],
          };
        if (typeof rightArg.value !== 'number')
          return {
            success: false,
            position: rightArg.extents.start,
            errors: [
              {
                message: `Expected a number on the right side of >=, but got ${this.input.slice(rightArg.extents.start, rightArg.extents.end).trim()} (a ${typeof rightArg.value}) instead.`,
                correction: {
                  replaceFrom: rightArg.extents.start,
                  replaceTo: rightArg.extents.end,
                  replaceWith: '1 ',
                },
              },
            ],
            missingFields: [],
          };
        retval = leftArg.value >= rightArg.value;
        break;

      case '<=':
        // Make sure both arguments are numbers.
        if (typeof leftArg.value !== 'number')
          return {
            success: false,
            position: leftArg.extents.start,
            errors: [
              {
                message: `Expected a number on the left side of <=, but got ${this.input.slice(leftArg.extents.start, leftArg.extents.end).trim()} (a ${typeof leftArg.value}) instead.`,
                correction: {
                  replaceFrom: leftArg.extents.start,
                  replaceTo: leftArg.extents.end,
                  replaceWith: '0 ',
                },
              },
            ],
            missingFields: [],
          };
        if (typeof rightArg.value !== 'number')
          return {
            success: false,
            position: rightArg.extents.start,
            errors: [
              {
                message: `Expected a number on the right side of <=, but got ${this.input.slice(rightArg.extents.start, rightArg.extents.end).trim()} (a ${typeof rightArg.value}) instead.`,
                correction: {
                  replaceFrom: rightArg.extents.start,
                  replaceTo: rightArg.extents.end,
                  replaceWith: '1 ',
                },
              },
            ],
            missingFields: [],
          };
        retval = leftArg.value <= rightArg.value;
        break;

      default:
        throw new Error();
    }

    // Reformat NaN to a reasonable thing users can Google. This will show up with a bunch of Excel help articles which will give them useful insight.
    if (Number.isNaN(retval)) retval = '#NAN';

    return {
      success: true,
      value: retval,
      extents: { start: leftArg.extents.start, end: rightArg.extents.end },
      identifiers: [...leftArg.identifiers, ...rightArg.identifiers],
    };
  }
}

/** Calculates the Levenshtein distance between two strings. */
function EditDistance(left: string, right: string): number {
  // Memo matrix to save on time.
  const memo: number[][] = Array(left.length).fill(Array(right.length).fill(-1));

  function lev(lIdx: number, rIdx: number): number {
    if (memo[lIdx]?.[rIdx] !== undefined && memo[lIdx][rIdx] >= 0) return memo[lIdx][rIdx]; // Cached.
    let res;

    if (Math.min(lIdx, rIdx) < 0) {
      // One string empty; edit distance is just inserting what's left.
      return Math.max(lIdx, rIdx) + 1;
    } else if (left[lIdx] === right[rIdx]) {
      // Characters match. Continue.
      res = lev(lIdx - 1, rIdx - 1);
    } else {
      // Mismatch.
      res =
        1 +
        Math.min(
          lev(lIdx - 1, rIdx), // Deletion
          lev(lIdx, rIdx - 1), // Insertion
          lev(lIdx - 1, rIdx - 1), // Substitution
        );
    }

    if (memo[lIdx]) {
      memo[lIdx][rIdx] = res;
    }
    return res;
  }

  return lev(left.length - 1, right.length - 1);
}
